import type { WorkOrder } from "../../queries/useWorkOrdersQuery";

/**
 * Statuses a Work Order can never leave through a bulk mutation: completion,
 * cancellation, and deletion are all terminal in the backend's status model.
 */
const TERMINAL_STATUS_CODES = new Set(["COMPLETED", "CANCELED", "DELETED"]);

/**
Statuses a Work Order can be completed from. There is no required
 * lifecycle order, so Open completes directly.
 */
const COMPLETABLE_STATUS_CODES = new Set(["OPEN", "IN_PROGRESS"]);

/**
 * Mirrors the backend's `canCompleteWorkOrder` gate (status must be `OPEN` or
 * `IN_PROGRESS` and the caller must be the assignee, an active team member, or
 * an Administrator, which the list response already resolves into
 * `canComplete`). Open
 * Procedures are checked by the backend when completion is requested and come
 * back as a per-item failure.
 */
export function isEligibleForCompletion(workOrder: WorkOrder): boolean {
  return (
    COMPLETABLE_STATUS_CODES.has(workOrder.status.code) && workOrder.canComplete
  );
}

export function completionIneligibilityReason(workOrder: WorkOrder): string {
  if (!COMPLETABLE_STATUS_CODES.has(workOrder.status.code)) {
    return "Only open or in-progress Work Orders can be marked as done.";
  }
  return "Only the assigned user or an active team member can complete this Work Order.";
}

/** Mirrors the backend's "Only incomplete Work Orders can be canceled." rule. */
export function isEligibleForCancellation(workOrder: WorkOrder): boolean {
  return !TERMINAL_STATUS_CODES.has(workOrder.status.code);
}

export function cancellationIneligibilityReason(): string {
  return "This Work Order is already completed, canceled, or deleted.";
}

export function isEligibleForAssignment(
  workOrder: WorkOrder,
  currentUserId: number | null,
): boolean {
  if (TERMINAL_STATUS_CODES.has(workOrder.status.code)) return false;
  if (currentUserId !== null && workOrder.assignee?.id === currentUserId) {
    return false;
  }
  return true;
}

export function assignmentIneligibilityReason(
  workOrder: WorkOrder,
  currentUserId: number | null,
): string {
  if (currentUserId !== null && workOrder.assignee?.id === currentUserId) {
    return "Already assigned to you.";
  }
  return "This Work Order is already completed, canceled, or deleted.";
}
